import { useMemo } from 'react';

import { useCallApi } from '@hooks/useCallApi';
import { useQuery as useOldQuery } from '@tanstack/react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

export function createUseQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TQueryParam = undefined,
>(
  queryKey: TQueryKey | ((p: TQueryParam) => TQueryKey),
  createQueryFn: (p: TQueryParam) => QueryFunction<TQueryFnData, TQueryKey>,
  defaultOptions?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
  type ParamIgnorableResult = (
    p?: TQueryParam,
    options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  ) => UseQueryResult<TData, TError>;

  type Result = (
    p: TQueryParam,
    options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  ) => UseQueryResult<TData, TError>;

  type FinalResult = TQueryParam extends undefined ? ParamIgnorableResult : Result;

  return ((
    p?: TQueryParam,
    options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  ): UseQueryResult<TData, TError> => {
    const finalOptions = useMemo(
      () => ({
        ...defaultOptions,
        ...options,
      }),
      [options],
    );

    const finalKey = queryKey instanceof Function ? queryKey(p!) : queryKey;
    const finalFn = useCallApi(createQueryFn(p!), finalKey);

    const query = useOldQuery(finalKey, finalFn, finalOptions);

    return query;
  }) as FinalResult;
}
